import React, { useState, Fragment, useEffect, useRef } from "react";
import { SiteData } from "../Data/SiteData";
import ReactDOM, { unmountComponentAtNode } from "react-dom";
import VGPopUp from "components/Controls/VGPopUp";
import { Form } from "react-bootstrap";
import FontFileUpload from "./FontFileUpload";
import Loader from "components/Common/Loader";

import { utils } from "utils/Utils";
import { AsyncAjaxPost, AsyncAjaxDelete } from "components/Common/AsyncAjaxService";
interface CustomFontUploadProps {
  siteData: SiteData;
}

export default function CustomFontUpload({ siteData }: CustomFontUploadProps) {
  const azContainer = siteData.azContainer;
  const azContainerUrlOverride = siteData.azContainerURL;
  const [uploadedFonts, setUploadedFonts] = useState<string[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [loadingFonts, setLoadingFonts] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [editingFont, setEditingFont] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshFontStyles = () => {
    siteData.isStylePublishDirty = true;
    siteData.isStyleSaveDirty = true;

    siteData.onUpdateStyle();

    if (siteData.designer) {
      siteData.designer.updateMasterLayout();
    }

    try {
      document.dispatchEvent(
        new CustomEvent("vagaro-fonts-changed", {
          detail: { timestamp: Date.now() },
        })
      );
    } catch (e) {
      console.warn("Failed to dispatch font change event:", e);
    }
  };

  const injectCustomFontStyles = () => {
    if (!siteData?.siteStyling?.customFonts) return;

    siteData.siteStyling.customFonts.forEach((fontStr) => {
      try {
        if (
          !fontStr ||
          typeof fontStr !== "string" ||
          fontStr.startsWith("family=")
        ) {
          return;
        }

        const fontData = JSON.parse(fontStr);
        if (fontData.cssDeclaration) {
          const fontId = `font-${fontData.family.replace(/\s+/g, "-")}`;
          if (!document.getElementById(fontId)) {
            const style = document.createElement("style");
            style.id = fontId;
            style.textContent = fontData.cssDeclaration;
            document.head.appendChild(style);
          }
        }
      } catch (e) {
        // Skip invalid entries
      }
    });
  };

  // Initialize uploadedFonts from siteData on component mount
  useEffect(() => {
    if (siteData?.siteStyling?.customFonts?.length > 0) {
      const fontsWithNulls = siteData.siteStyling.customFonts.map((fontStr) => {
        try {
          if (
            !fontStr ||
            typeof fontStr !== "string" ||
            fontStr.trim() === ""
          ) {
            return null;
          }

          // If it starts with "family=", it's the option format, not JSON
          if (fontStr.startsWith("family=")) {
            return null;
          }

          const fontData = JSON.parse(fontStr);
          if (
            fontData &&
            fontData.family &&
            typeof fontData.family === "string"
          ) {
            const format =
              fontData.format === "truetype"
                ? "ttf"
                : fontData.format === "opentype"
                ? "otf"
                : fontData.format || "ttf"; // fallback to ttf
            return `${fontData.family}.${format}`;
          }
          return null;
        } catch (e) {
          return null;
        }
      });

      const fonts: string[] = fontsWithNulls.filter(Boolean) as string[];
      setUploadedFonts(fonts);

      injectCustomFontStyles();
    } else {
      setUploadedFonts([]);
    }
  }, [siteData?.siteStyling?.customFonts]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!file || !(file instanceof File)) {
        reject(new Error("Invalid file provided"));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const result = reader.result as string;

          if (!result || typeof result !== "string") {
            reject(new Error("Failed to read file as data URL"));
            return;
          }

          // Remove data URL prefix (data:font/ttf;base64,)
          const parts = result.split(",");
          if (parts.length < 2) {
            reject(new Error("Invalid data URL format"));
            return;
          }

          const base64 = parts[1];

          if (!base64 || base64.length === 0) {
            reject(new Error("Empty base64 data"));
            return;
          }

          resolve(base64);
        } catch (error) {
          reject(
            new Error(
              `Failed to process file: ${
                error instanceof Error ? error.message : "Unknown error"
              }`
            )
          );
        }
      };
      reader.onerror = () => reject(new Error("FileReader error occurred"));
      reader.readAsDataURL(file);
    });
  };

  const handleFontUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setLoading(true);

    try {
      const processedFonts: { name: string; ext: string }[] = [];

      for (const file of files) {
        if (!isValidFontFile(file, editingFont)) {
          console.warn(`Skipping invalid font file: ${file.name}`);
          continue;
        }

        // Get file extension safely
        const lastDotIndex = file.name.lastIndexOf(".");
        if (lastDotIndex === -1) {
          console.warn(`Skipping file without extension: ${file.name}`);
          continue;
        }

        const fontNameWithoutExt = file.name.substring(0, lastDotIndex);
        const fileExt = file.name.substring(lastDotIndex).toLowerCase();
        const fileExtClean = fileExt.startsWith(".")
          ? fileExt.substring(1)
          : fileExt;

        // Validate font name is not empty
        if (!fontNameWithoutExt.trim()) {
          console.warn(`Skipping file with empty name: ${file.name}`);
          continue;
        }

        const base64Font = await fileToBase64(file);

        // Upload font to server using confirmed working raw base64 format
        await new Promise<void>((resolve, reject) => {
          AsyncAjaxPost(
            "merchants/mysitefont/upload",
            {
              FileName: file.name,
              base64image: base64Font,
            },
            null,
            () => {
              resolve();
            },
            (error: any) => {
              reject(
                new Error(
                  `Font upload failed: ${
                    error?.response?.data?.message ||
                    error.message ||
                    "Unknown error"
                  }`
                )
              );
            },
            siteData.userContext.businessId,
            null
          );
        });

        const fontUrl = `${azContainerUrlOverride}/mysite/Font/${azContainer}/${file.name}`;

        const originalFontName = editingFont ? editingFont.split(".")[0] : null;

        // Create the font data object
        const fontFaceDeclaration = `@font-face {
          font-family: "${fontNameWithoutExt}";
          src: url("${fontUrl}") format("${getFormatFromExtension(fileExt)}");
          font-weight: normal;
          font-style: normal;
        }`;

        const fontData = {
          family: fontNameWithoutExt,
          url: fontUrl,
          format: getFormatFromExtension(fileExt),
          cssDeclaration: fontFaceDeclaration,
        };

        const fontOptionFormat = `family=${fontNameWithoutExt}`;

        if (!siteData.siteStyling.customFonts) {
          siteData.siteStyling.customFonts = [];
        }

        if (editingFont && originalFontName) {
          let fontIndex = -1;
          let fontOptionIndex = -1;

          siteData.siteStyling.customFonts.forEach((fontStr, index) => {
            try {
              if (!fontStr || typeof fontStr !== "string") return;

              const fontData = JSON.parse(fontStr);
              if (fontData && fontData.family === originalFontName) {
                fontIndex = index;
              }
            } catch (e) {
              if (
                typeof fontStr === "string" &&
                fontStr.startsWith("family=") &&
                fontStr.includes(originalFontName)
              ) {
                fontOptionIndex = index;
              }
            }
          });

          siteData.siteStyling.customFonts =
            siteData.siteStyling.customFonts.filter((fontDataStr) => {
              try {
                if (!fontDataStr || typeof fontDataStr !== "string")
                  return true;

                const fontData = JSON.parse(fontDataStr);
                return fontData.family !== originalFontName;
              } catch (error) {
                if (fontDataStr.startsWith("family=")) {
                  return !fontDataStr.includes(`family=${originalFontName}`);
                }
                return !fontDataStr.includes(originalFontName);
              }
            });

          // Insert the new font data at the original position or unshift if position not found
          const fontDataJson = JSON.stringify(fontData);
          const fontOptionStr = `family=${fontNameWithoutExt}`;

          if (
            fontIndex !== -1 &&
            fontIndex < siteData.siteStyling.customFonts.length
          ) {
            // Insert at same position as the original JSON data
            siteData.siteStyling.customFonts.splice(fontIndex, 0, fontDataJson);
          } else {
            // Fall back to unshift if position can't be found
            siteData.siteStyling.customFonts.unshift(fontDataJson);
          }

          // Add the option format string if needed
          if (!siteData.siteStyling.customFonts.includes(fontOptionStr)) {
            if (
              fontOptionIndex !== -1 &&
              fontOptionIndex < siteData.siteStyling.customFonts.length
            ) {
              siteData.siteStyling.customFonts.splice(
                fontOptionIndex,
                0,
                fontOptionStr
              );
            } else {
              const newIndex =
                siteData.siteStyling.customFonts.indexOf(fontDataJson) + 1;
              if (
                newIndex > 0 &&
                newIndex <= siteData.siteStyling.customFonts.length
              ) {
                siteData.siteStyling.customFonts.splice(
                  newIndex,
                  0,
                  fontOptionStr
                );
              } else {
                siteData.siteStyling.customFonts.push(fontOptionStr);
              }
            }
          }

          // Update font selections if they reference the old font
          if (siteData.siteStyling.fontPrimary === originalFontName) {
            siteData.siteStyling.fontPrimary = fontNameWithoutExt;
          }

          if (siteData.siteStyling.fontSecondary === originalFontName) {
            siteData.siteStyling.fontSecondary = fontNameWithoutExt;
          }
        } else {
          // Adding new font
          const fontDataJson = JSON.stringify(fontData);
          siteData.siteStyling.customFonts.unshift(fontDataJson);

          if (!siteData.siteStyling.customFonts.includes(fontOptionFormat)) {
            siteData.siteStyling.customFonts.unshift(fontOptionFormat);
          }
        }

        processedFonts.push({ name: fontNameWithoutExt, ext: fileExtClean });
      }

      // Update the UI with all new fonts
      if (editingFont && processedFonts.length > 0) {
        setUploadedFonts((prev) =>
          prev.map((font) =>
            font === editingFont
              ? `${processedFonts[0].name}.${processedFonts[0].ext}`
              : font
          )
        );
        setEditingFont(null);
      } else if (processedFonts.length > 0) {
        // Add all new fonts to the beginning of the list (in reverse to maintain order)
        setUploadedFonts((prev) => [
          ...processedFonts.map((f) => `${f.name}.${f.ext}`).reverse(),
          ...prev,
        ]);
      }

      refreshFontStyles();
      injectCustomFontStyles();

      setTimeout(() => {
        setLoading(false);

        if (processedFonts.length > 1) {
          showAlert(
            "Fonts Added",
            `Successfully added ${processedFonts.length} fonts.`
          );
        }
      }, 100);
    } catch (error) {
      showAlert(
        "Upload Failed",
        "Could not upload font files. Please try again."
      );
      setLoading(false);
      setEditingFont(null);
    }
  };

  const getFormatFromExtension = (ext: string) => {
    switch (ext) {
      case ".woff2":
        return "woff2";
      case ".woff":
        return "woff";
      case ".otf":
        return "opentype";
      default:
        return "truetype";
    }
  };

  const deleteFontConfirmation = (fontName: string) => {
    if (!fontName || typeof fontName !== "string") {
      console.warn("Invalid font name for deletion");
      return;
    }

    const popupClose = () => {
      let dom = document.getElementById("OpenDialog2");
      if (dom) unmountComponentAtNode(dom);
    };

    const dom = document.getElementById("OpenDialog2");
    if (!dom) {
      console.warn("OpenDialog2 element not found");
      return;
    }

    ReactDOM.render(
      <VGPopUp
        id={"DeleteFontConfirmation"}
        show={true}
        onHide={popupClose}
        title={"Delete Custom Font"}
        dialogClassName="cancelauto-renew-alert"
        body={
          <div className="alertContent">
            <p>
              This font will be removed from your website. Are you sure you want
              to remove <strong> {fontName.split(".")[0]}</strong>?
            </p>
          </div>
        }
        footer={
          <Fragment>
            <button
              className="w-button w-button-secondary"
              onClick={() => popupClose()}
            >
              Cancel
            </button>
            <button
              className="w-button w-button-destructive-outline"
              onClick={() => deleteFont(fontName, popupClose)}
            >
              Delete
            </button>
          </Fragment>
        }
        isRemoveTabModalIndex={false}
        isShowFooter={true}
        size={"modal-sm"}
        backdrop={true}
      />,
      dom
    );
  };

  const deleteFont = async (fontName: string, popupClose: () => void) => {
    setLoadingFonts((prev) => ({ ...prev, [fontName]: true }));

    try {
      const fontNameWithoutExt = fontName.split(".")[0];

      await new Promise<void>((resolve, reject) => {
        AsyncAjaxDelete(
          `merchants/mysitefont/delete?filename=${fontName}`,
          null,
          () => {
            resolve();
          },
          (error: any) => {
            reject(error);
          },
          siteData.userContext.businessId,
          null
        );
      });

      // Clean up after successful deletion
      // Check if the deleted font is being used as primary or secondary font
      if (siteData.siteStyling.fontPrimary === fontNameWithoutExt) {
        siteData.siteStyling.fontPrimary = ""; // Reset to "Auto"
      }

      if (siteData.siteStyling.fontSecondary === fontNameWithoutExt) {
        siteData.siteStyling.fontSecondary = ""; // Reset to "Auto"
      }

      const updatedFonts = siteData.siteStyling.customFonts.filter(
        (fontDataStr) => {
          try {
            const fontData = JSON.parse(fontDataStr);
            return fontData.family !== fontNameWithoutExt;
          } catch (error) {
            if (fontDataStr.startsWith("family=")) {
              return !fontDataStr.includes(`family=${fontNameWithoutExt}`);
            }
            return !fontDataStr.includes(fontNameWithoutExt);
          }
        }
      );

      siteData.siteStyling.customFonts = updatedFonts;

      // Remove the font's style element from the document
      const fontId = `font-${fontNameWithoutExt.replace(/\s+/g, "-")}`;
      const styleElement = document.getElementById(fontId);
      if (styleElement) {
        styleElement.remove();
      }

      refreshFontStyles();

      setTimeout(() => {
        setUploadedFonts((prev) => prev.filter((font) => font !== fontName));
        popupClose();
        setLoadingFonts((prev) => {
          const updated = { ...prev };
          delete updated[fontName]; // Clean up instead of setting to false
          return updated;
        });
      }, 500);
    } catch (error) {
      showAlert("Delete Failed", "Could not delete font. Please try again.");
      setLoadingFonts((prev) => ({ ...prev, [fontName]: false }));
    }
  };

  const isValidFontFile = (
    file: File,
    editingFontName: string | null = null
  ): boolean => {
    // Basic file validation
    if (!file || !(file instanceof File)) {
      console.warn("Invalid file object");
      return false;
    }

    if (file.size === 0) {
      console.warn("Empty file not allowed");
      return false;
    }

    // Check file size limit (e.g., 10MB)
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeBytes) {
      utils.ShowAlert(
        "File Too Large",
        `Font file must be smaller than 10MB. Current size: ${(
          file.size /
          (1024 * 1024)
        ).toFixed(1)}MB`,
        "OpenDialog2"
      );
      return false;
    }

    // Validate filename
    const lastDotIndex = file.name.lastIndexOf(".");
    if (lastDotIndex === -1) {
      utils.ShowAlert(
        "Invalid File",
        "Font file must have a valid extension (.ttf, .otf, .woff, .woff2)",
        "OpenDialog2"
      );
      return false;
    }

    const fileExt = file.name.substring(lastDotIndex).toLowerCase();
    const newFontName = file.name.substring(0, lastDotIndex).trim();

    // Validate font name
    if (!newFontName) {
      utils.ShowAlert(
        "Invalid File Name",
        "Font file must have a valid name",
        "OpenDialog2"
      );
      return false;
    }

    // Check for invalid characters in font name
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(newFontName)) {
      utils.ShowAlert(
        "Invalid Font Name",
        "Font name contains invalid characters. Please rename the file.",
        "OpenDialog2"
      );
      return false;
    }

    const originalFontName = editingFontName
      ? editingFontName.split(".")[0]
      : null;

    // Check for duplicate font names
    if (siteData?.siteStyling?.customFonts) {
      for (const fontStr of siteData.siteStyling.customFonts) {
        try {
          if (!fontStr || typeof fontStr !== "string") continue;

          const fontData = JSON.parse(fontStr);

          if (
            editingFontName &&
            fontData &&
            fontData.family === originalFontName
          ) {
            continue; // Skip the font we're editing
          }

          if (fontData && fontData.family === newFontName) {
            utils.ShowAlert(
              "Duplicate Font",
              `A font named "${newFontName}" already exists.`,
              "OpenDialog2"
            );
            return false;
          }
        } catch (e) {
          continue; // Skip invalid JSON entries
        }
      }
    }

    // Validate file extension
    const validExtensions = [".ttf", ".otf", ".woff", ".woff2"];
    if (!validExtensions.includes(fileExt)) {
      utils.ShowAlert(
        "Invalid File Type",
        `Supported formats: ${validExtensions.join(", ")}`,
        "OpenDialog2"
      );
      return false;
    }

    return true;
  };

  const showAlert = (title: string, message: string) => {
    if (!title || !message) {
      console.warn("Invalid alert parameters");
      return;
    }

    const alertClose = () => {
      let dom = document.getElementById("OpenDialog2");
      if (dom) unmountComponentAtNode(dom);
    };

    const dom = document.getElementById("OpenDialog2");
    if (!dom) {
      console.warn("OpenDialog2 element not found for alert");
      return;
    }

    ReactDOM.render(
      <VGPopUp
        id={"FontAlert"}
        show={true}
        onHide={alertClose}
        title={title}
        dialogClassName="alert"
        body={
          <div className="alertContent">
            <p>{message}</p>
          </div>
        }
        footer={
          <Fragment>
            <button
              className="w-button w-button-primary"
              onClick={() => alertClose()}
            >
              Ok
            </button>
          </Fragment>
        }
        isRemoveTabModalIndex={false}
        isShowFooter={true}
        size={"modal-sm"}
        backdrop={true}
      />,
      dom
    );
  };

  const editFontTrigger = (fontName: string) => {
    if (!fontName || typeof fontName !== "string") {
      console.warn("Invalid font name for editing");
      return;
    }

    setEditingFont(fontName);

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="custom-font-upload" style={{ position: "relative" }}>
      {isLoading && (
        <div
          className="loading-overlay"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loader />
        </div>
      )}

      <div id="OpenDialog2"></div>

      {/* Existing Fonts */}
      {uploadedFonts.length > 0 && (
        <div className="mb-4">
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h4>Your Custom Fonts</h4>
          </div>
          {uploadedFonts.map((font, index) => {
            // Safely split font name and extension
            const fontParts = font.split(".");
            const fontNameDisplay = fontParts[0] || "Unknown";
            const fontExtDisplay = fontParts[1] || "unknown";

            return (
              <div
                key={`${font}-${index}`} // More unique key
                className="drop-file-upload py-t8 uploaded font-preview"
                style={{
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                }}
              >
                {loadingFonts[font] && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      zIndex: 5,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Loader size="sm" />
                  </div>
                )}

                <div
                  className="font-preview-section"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    paddingLeft: "24px",
                  }}
                >
                  <div className="font-name">
                    <span
                      style={{ fontFamily: fontNameDisplay, fontWeight: 500 }}
                    >
                      {fontNameDisplay}
                    </span>
                    <span
                      className="text-muted ml-1"
                      style={{ fontSize: "1em" }}
                    >
                      .{fontExtDisplay}
                    </span>
                  </div>
                </div>

                <div className="showdelete d-flex">
                  <div className="p-2">
                    <button
                      className="w-button w-button-secondary"
                      onClick={() => editFontTrigger(font)}
                      disabled={loadingFonts[font]}
                    >
                      Edit
                    </button>
                  </div>
                  <div className="p-2">
                    <button
                      className="w-button w-button-destructive-outline"
                      onClick={() => deleteFontConfirmation(font)}
                      disabled={loadingFonts[font]}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="p-2"></div>
        </div>
      )}

      {/* Upload New Font */}
      <div className="drop-file-upload py-t8 ">
        <Form className="form-content">
          <Form.Group className={`form-row`}>
            <Form.Label>
              Upload a New Font File to use on your website:
            </Form.Label>
            <p className="form-text mb-3">
              File types supported: .woff2, .woff .ttf or .otf
            </p>

            <div
              className={`drop-file-upload webclip py-t8`}
              style={{ margin: "16px auto", marginTop: "32px" }}
            >
              <i className="far fa-cloud-upload" />
              <span className="upload-note">Drag and drop files here or</span>
              <span className="upload-trigger">Choose File</span>
              <FontFileUpload
                id="generalupload"
                className={"inpWebclip"}
                onUpload={handleFontUpload}
                setLoading={setLoading}
                ref={fileInputRef}
              ></FontFileUpload>
            </div>
          </Form.Group>
        </Form>
      </div>

      {/* <p className="mt-3 form-text">
        Note: Custom fonts may affect website performance. Use sparingly for best results.
      </p> */}
    </div>
  );
}
